import {
  base64toBlob,
  calculateAspectRatioFit,
  getImgDimensions,
  getImgSize,
} from "MyDashboards/utils/utils";
import { useRef, useState } from "react";
import Resizer from "react-image-file-resizer";
import sha256 from "../utils/sha256";
import uploadFile from "../utils/uploadFile";

/**
 * This function will convert to png Then scale image down to under 10 mb (preserving aspect ratio) then upload
 */
const useUploadSlideImage = () => {
  const [isLoading, setisLoading] = useState(false);
  const [isError, setisError] = useState(false);
  const [data, setdata] = useState<null | string>(null);
  const errorMsg = useRef<null | string>(null);
  const webSlideToPPTRatio = 2;

  const isImgOfCorrectType = (base64: string): boolean => {
    return Boolean(base64.match(/data:image\/png;base64/gim));
  };

  /**
   * either resizes the image if it's larger than 10mb or return it as is if not
   */
  const getReducedImgSize = async (
    base64: string
  ): Promise<{ base64: string; newWidth: number; newHeight: number }> => {
    const tenMb = 10048576000000;
    const imgSize = getImgSize(base64);
    const { width, height } = await getImgDimensions(base64);
    if (imgSize <= tenMb)
      return { base64: base64, newWidth: width, newHeight: height };

    const reductionPercentage = 10 / imgSize;
    const newDimensions = calculateAspectRatioFit(
      width,
      height,
      reductionPercentage * width,
      reductionPercentage * height
    );
    const blob = await base64toBlob(base64);
    return new Promise<{ base64: string; newWidth: number; newHeight: number }>(
      (resolve, err) =>
        Resizer.imageFileResizer(
          blob, // Is the file of the image which will resized.
          newDimensions.width * webSlideToPPTRatio, // Is the maxWidth of the resized new image.
          newDimensions.height * webSlideToPPTRatio, // Is the maxHeight of the resized new image.
          "PNG", // Is the compressFormat of the resized new image.
          100, // Is the quality of the resized new image.
          0, // Is the degree of clockwise rotation to apply to uploaded image.
          (res: any) => {
            resolve({
              base64: res as string,
              newWidth: newDimensions.width,
              newHeight: newDimensions.height,
            });
          }, // Is the callBack function of the resized new image URI.
          "base64", // Is the output type of the resized new image.
          newDimensions.width * webSlideToPPTRatio, // Is the minWidth of the resized new image.
          newDimensions.height * webSlideToPPTRatio // Is the minHeight of the resized new image.
        )
    );
  };

  const uploadSlideImage = async (
    base64: string
  ): Promise<{ ref: string; newWidth: number; newHeight: number } | null> => {
    setisLoading(true);
    if (!isImgOfCorrectType) {
      errorMsg.current = "Image type not correct. uploading failed";
      setisError(true);
      return null;
    }

    const {
      base64: reducedSizeImgb64,
      newWidth,
      newHeight,
    } = await getReducedImgSize(base64);
    const start = `data:image/png;base64,`;
    const base64ImageData = reducedSizeImgb64.slice(start.length);
    const sha = await sha256(base64ImageData);
    const ref = `${sha}.png`;
    try {
      await uploadFile({
        hashFileName: ref,
        fileContent: base64ImageData,
      });
      setisLoading(false);
      setisError(false);
      setdata(ref);
      errorMsg.current = null;
      return { ref: ref, newWidth: newWidth, newHeight: newHeight };
    } catch (e) {
      setisLoading(false);
      setisError(true);
      setdata(null);
      errorMsg.current = `${e}`;
      throw e;
    }
  };

  return { uploadSlideImage, isLoading, isError, data, errorMsg };
};

export default useUploadSlideImage;
